import React, { createContext, useContext, useEffect, useState } from "react";
import { isSupabaseConfigured, supabase } from "../lib/supabase";

const AuthContext = createContext(null);

const functionErrorKey = (error, fallback) => {
  const message = String(error?.message || error || "").toLowerCase();
  if (message.includes("failed to send a request") || message.includes("functionsfetcherror") || message.includes("404")) {
    return "auth.telegram_service_unavailable";
  }
  return fallback;
};

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarFeaturesReady, setAvatarFeaturesReady] = useState(false);

  const loadProfile = async (authUser) => {
    if (!authUser || !supabase) {
      setProfile(null);
      return null;
    }
    let { data, error } = await supabase
      .from("profiles")
      .select("id, full_name, role, roles, guide_slug, is_approved, avatar_url")
      .eq("id", authUser.id)
      .maybeSingle();
    setAvatarFeaturesReady(!error);
    if (error?.code === "42703" || error?.code === "PGRST204") {
      ({ data } = await supabase
        .from("profiles")
        .select("id, full_name, role, guide_slug, is_approved")
        .eq("id", authUser.id)
        .maybeSingle());
    }
    const normalizedProfile = data ? {
      ...data,
      roles: Array.isArray(data.roles) ? data.roles : [...new Set(["client", data.role].filter(Boolean))],
    } : null;
    setProfile(normalizedProfile);
    return normalizedProfile;
  };

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return undefined;
    }

    supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session);
      await loadProfile(data.session?.user);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      loadProfile(nextSession?.user).finally(() => setLoading(false));
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  const register = async ({ name, email, password }) => {
    if (!supabase) return { success: false, errorKey: "auth.not_configured" };
    const { data, error } = await supabase.functions.invoke("telegram-register", {
      body: { name, email, password },
    });
    if (error || !data?.success) {
      let responseBody = data;
      if (error && !responseBody) {
        try {
          responseBody = await error.context?.json();
        } catch {
          // Keep the generic message when the function response is unavailable.
        }
      }
      return { success: false, errorKey: responseBody?.errorKey || functionErrorKey(error, "auth.registration_failed") };
    }
    return { success: true, needsConfirmation: true, role: "client", ...data };
  };

  const confirmTelegramCode = async (token, code) => {
    if (!supabase) return { success: false, errorKey: "auth.not_configured" };
    const { data, error } = await supabase.functions.invoke("telegram-confirm", { body: { token, code } });
    if (error) {
      let responseBody = null;
      try {
        responseBody = await error.context?.json();
      } catch {
        // Keep the generic message when the function response is unavailable.
      }
      return {
        success: false,
        errorKey: responseBody?.errorKey || functionErrorKey(error, "auth.verification_failed"),
      };
    }
    return data;
  };

  const login = async (email, password) => {
    if (!supabase) return { success: false, errorKey: "auth.not_configured" };
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { success: false, errorKey: "auth.invalid_credentials" };
    const nextProfile = await loadProfile(data.user);
    return { success: true, profile: nextProfile };
  };

  const logout = async () => {
    if (supabase) await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
  };

  const refreshProfile = () => loadProfile(session?.user);

  const user = session?.user
      ? (() => {
        const metadata = session.user.user_metadata || {};
        const fullName = profile?.full_name || metadata.full_name || session.user.email;
        // The database profile is authoritative. Auth metadata is only a
        // fallback while a profile is unavailable; never infer permissions
        // from a display name.
        const role = profile?.role || metadata.role || "client";
        return {
          ...session.user,
          name: fullName,
          ...profile,
          role,
          roles: profile?.roles || [...new Set(["client", role].filter(Boolean))],
          canTravel: (profile?.roles || ["client", role]).includes("client"),
        };
      })()
    : null;

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, avatarFeaturesReady, login, register, confirmTelegramCode, logout, refreshProfile, isConfigured: isSupabaseConfigured }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
